import { useMemo, useState } from "react";
import { ArrowLeft, Bookmark, Layers3, Play, ShoppingCart } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";

import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBundleItem } from "@/hooks/useBundles";
import { isPermissionExpired, useCoursePermissions } from "@/hooks/usePermissions";
import { useSEO } from "@/hooks/useSEO";
import { buildVimeoEmbedUrl, formatDuration } from "@/lib/helpers";
import { useAuth } from "@/lib/auth";
import type { BundleWithCourses, CartBundleItem, CourseWithDetails } from "@/lib/schema";
import { useAppStore } from "@/lib/store";
import { cn } from "@/lib/utils";

const signatureRoutineTagLabel = "Signature Routine";
const maxRoutineDetailTagCount = 5;

export function RoutinePage() {
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const { data: bundle, loading, error } = useBundleItem(id ?? null);
  const { data: permissions } = useCoursePermissions();
  const { addToCart, cartItems, isBundleSaved, setCartOpen, toggleSavedBundle } = useAppStore();
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  const checkoutStatus = useMemo(
    () => new URLSearchParams(location.search).get("checkout"),
    [location.search],
  );

  const activePermissionIds = useMemo(() => {
    return new Set(
      (permissions ?? [])
        .filter((permission) => !isPermissionExpired(permission))
        .map((permission) => permission.course_id.id),
    );
  }, [permissions]);

  const selectedCourse = useMemo(() => {
    if (!bundle || !selectedCourseId) return null;
    return bundle.courses.find((course) => course.id === selectedCourseId) ?? null;
  }, [bundle, selectedCourseId]);

  const bundleCartItems = useMemo(
    () => cartItems.filter((item): item is CartBundleItem => item.type === "bundle"),
    [cartItems],
  );

  const isOwned = useMemo(() => {
    if (!bundle || bundle.courses.length === 0) return false;
    return bundle.courses.every((course) => activePermissionIds.has(course.id));
  }, [activePermissionIds, bundle]);

  const inCart = id ? bundleCartItems.some((item) => item.bundle_id === id) : false;
  const hasCartConflict = bundle
    ? !inCart && bundleHasCartConflict(bundle, bundleCartItems)
    : false;
  const saved = id ? isBundleSaved(id) : false;

  useSEO({
    title: bundle?.name ?? "Routine",
    description: bundle?.short_description ?? undefined,
    canonical: id ? `/app/routine/${id}` : undefined,
    ogTitle: bundle?.name ?? undefined,
    ogDescription: bundle?.short_description ?? undefined,
    ogImage: bundle?.thumbnail_url ?? undefined,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-4 py-20 text-center sm:px-6">
          <div className="h-8 w-64 animate-pulse rounded bg-neutral-lightest" />
          <div className="h-4 w-96 animate-pulse rounded bg-neutral-lightest" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!bundle || error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-4 py-20 text-center sm:px-6">
          <h1 className="font-display text-2xl text-pink-darker sm:text-4xl">Routine not found</h1>
          <p className="text-neutral-dark">
            The requested routine is unavailable. Please browse the dance library.
          </p>
          <Button asChild className="rounded-round bg-pink-darker text-white hover:bg-pink-dark">
            <Link to="/dance-library">Go to Dance Library</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const instructorMeta = getBundleInstructorMeta(bundle);
  const categories = getBundleCategories(bundle, maxRoutineDetailTagCount - 1);
  const duration = getBundleDuration(bundle.courses);
  const totalVideos = getBundleTotalVideos(bundle.courses);
  const mediaVideoUrl = selectedCourse?.preview_video_id?.video_url ?? null;
  const mediaImage = selectedCourse?.thumbnail_links_course ?? bundle.thumbnail_url;
  const mediaTitle = selectedCourse?.name ?? bundle.name;
  const primaryLabel = getPrimaryLabel({ inCart, hasCartConflict });

  const handleAddToCart = () => {
    if (hasCartConflict) return;

    addToCart({
      type: "bundle",
      bundle_id: bundle.id,
      name: bundle.name,
      price: bundle.price,
      thumbnail_url: bundle.thumbnail_url ?? undefined,
      course_ids: bundle.courses.map((course) => course.id),
      course_count: bundle.courses.length,
    });
    setCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/dance-library?tags=Signature%20Routine"
          className="mb-6 inline-flex items-center gap-1 text-sm text-neutral-dark hover:text-pink-darker"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Dance Library
        </Link>

        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <section className="space-y-4">
            <div className="relative aspect-video overflow-hidden rounded-large bg-neutral-900">
              {mediaVideoUrl ? (
                <iframe
                  key={selectedCourse?.id}
                  src={buildVimeoEmbedUrl(mediaVideoUrl, { autoplay: true, muted: true })}
                  className="h-full w-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`Preview: ${mediaTitle}`}
                />
              ) : mediaImage ? (
                <>
                  <div
                    className="absolute inset-0 scale-125 bg-cover bg-center opacity-60 blur-[18px]"
                    style={{ backgroundImage: `url(${JSON.stringify(mediaImage)})` }}
                    aria-hidden="true"
                  />
                  <div className="absolute inset-0 bg-black/25" aria-hidden="true" />
                  <img
                    src={mediaImage}
                    alt={mediaTitle}
                    className="relative z-[1] h-full w-full object-contain"
                  />
                </>
              ) : (
                <div className="flex h-full items-center justify-center bg-[radial-gradient(circle_at_top,#dca4be_0%,#91557a_45%,#4f2343_100%)] text-white">
                  {selectedCourse ? <Play className="h-12 w-12 text-white/70" /> : <Layers3 className="h-14 w-14 text-white/70" />}
                </div>
              )}
            </div>

            <div className="rounded-main border border-neutral-lighter bg-white p-4">
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-neutral">
                    Included songs
                  </h3>
                  <p className="m-0 text-sm text-neutral-dark">
                    Click any song to play its video in the media panel.
                  </p>
                </div>
                {selectedCourse ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-round border-neutral-light text-neutral-dark hover:bg-neutral-lightest"
                    onClick={() => setSelectedCourseId(null)}
                  >
                    Show Routine Photo
                  </Button>
                ) : null}
              </div>

              <div className="space-y-2">
                {bundle.courses.map((course, index) => {
                  const active = selectedCourse?.id === course.id;
                  return (
                    <button
                      key={course.id}
                      type="button"
                      className={cn(
                        "flex w-full items-start justify-between gap-3 rounded-main border px-4 py-3 text-left transition-colors",
                        active
                          ? "border-pink-dark/35 bg-pink-dark/5 shadow-sm"
                          : "border-neutral-lighter bg-white hover:border-pink-dark/25 hover:bg-neutral-lightest/50",
                      )}
                      aria-pressed={active}
                      aria-label={`Play song ${index + 1}: ${course.name}`}
                      onClick={() => setSelectedCourseId(course.id)}
                    >
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral">
                          Song {index + 1}
                        </div>
                        <div className="mt-1 font-semibold text-pink-darker">{course.name}</div>
                        {(course.short_description ?? course.long_description) ? (
                          <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-neutral-dark">
                            {course.short_description ?? course.long_description}
                          </p>
                        ) : null}
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded-round px-3 py-1 text-xs font-semibold",
                          active ? "bg-pink-darker text-white" : "bg-neutral-lightest text-neutral-dark",
                        )}
                      >
                        {course.preview_video_id?.video_url ? "Play video" : "Show cover"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {bundle.long_description && (
              <div className="rounded-main border border-neutral-lighter bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral">
                  About this routine
                </h3>
                <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-neutral-dark">
                  {bundle.long_description}
                </p>
              </div>
            )}
          </section>

          <aside className="space-y-4">
            <Card className="rounded-main border-neutral-lighter bg-white py-0 shadow-none">
              <CardContent className="space-y-4 px-5 py-5">
                <p className="text-xs font-semibold tracking-[0.12em] text-neutral uppercase">
                  Routine Detail
                </p>
                <h1 className="font-display text-2xl leading-tight text-pink-darker sm:text-4xl">
                  {bundle.name}
                </h1>
                <p className="text-sm text-neutral-dark">
                  {instructorMeta.label}: {instructorMeta.name}
                </p>

                {bundle.short_description && (
                  <p className="text-sm text-neutral-dark">{bundle.short_description}</p>
                )}

                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-round bg-neutral-lightest px-2.5 py-1 text-neutral-dark">
                    {signatureRoutineTagLabel}
                  </span>
                  {categories.map((category) => (
                    <span
                      key={category}
                      className="rounded-round bg-neutral-lightest px-2.5 py-1 text-neutral-dark"
                    >
                      {category}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-neutral-dark">
                  <span>
                    {bundle.courses.length} song{bundle.courses.length === 1 ? "" : "s"}
                  </span>
                  {duration && <span>{duration} total</span>}
                  {totalVideos != null && (
                    <span>
                      {totalVideos} video{totalVideos === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                <p className="text-2xl font-semibold text-pink-dark">${bundle.price}</p>

                {checkoutStatus === "success" && (
                  <div className="rounded-main border border-blue-brand/20 bg-blue-brand/10 p-4 text-sm text-blue-brand">
                    Payment successful! The routine has been added to your dashboard.
                  </div>
                )}

                {checkoutStatus === "failed" && (
                  <div className="rounded-main border border-pink-dark/20 bg-pink-dark/5 p-4 text-sm text-pink-darker">
                    Checkout returned with a failed payment status. Please try again.
                  </div>
                )}

                {isOwned ? (
                  <div className="space-y-3">
                    <div className="rounded-main border border-blue-brand/20 bg-blue-brand/10 p-4 text-sm font-semibold text-blue-brand">
                      Routine unlocked
                    </div>
                    <Button
                      asChild
                      className="w-full rounded-round bg-pink-darker text-white hover:bg-pink-dark"
                    >
                      <Link to="/app/dashboard/home">Go to Dashboard</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Button
                      type="button"
                      className={cn(
                        "w-full rounded-round bg-pink-darker text-white hover:bg-pink-dark",
                        inCart && "border border-[#762a5c] bg-white text-[#762a5c] hover:bg-[#faf1f6]",
                        hasCartConflict && "cursor-not-allowed border border-neutral-light bg-neutral-lightest text-neutral hover:bg-neutral-lightest",
                      )}
                      disabled={hasCartConflict}
                      onClick={handleAddToCart}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      {primaryLabel}
                    </Button>
                    {!user && (
                      <p className="text-center text-xs text-neutral">
                        <Link
                          to="/app/auth/login"
                          className="text-blue-brand hover:underline"
                        >
                          Log in
                        </Link>{" "}
                        to checkout
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-round border border-neutral-light px-4 py-2.5 text-sm font-medium text-neutral-dark transition-colors hover:bg-neutral-lightest"
                  onClick={() => toggleSavedBundle(bundle.id)}
                >
                  <Bookmark
                    className={cn("h-4 w-4", saved && "fill-pink-darker text-pink-darker")}
                  />
                  {saved ? "Saved" : "Save for Later"}
                </button>
              </CardContent>
            </Card>

            <Card className="rounded-main border-neutral-lighter bg-white py-0 shadow-none">
              <CardContent className="space-y-2 px-5 py-5 text-sm text-neutral-dark">
                <p className="font-semibold text-neutral-darker">Included with this signature routine</p>
                <p>Performance-ready song sequence</p>
                <p>MP3 music mix</p>
                <p>Easy-to-share practice link</p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function getPrimaryLabel({
  inCart,
  hasCartConflict,
}: {
  inCart: boolean;
  hasCartConflict: boolean;
}) {
  if (inCart) return "Added to Cart";
  if (hasCartConflict) return "Resolve Cart to Add";
  return "Add to Cart";
}

function getBundleInstructorMeta(bundle: BundleWithCourses) {
  const instructorNames = Array.from(
    new Set(
      bundle.courses
        .map((course) => course.Instructor?.name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  );

  if (instructorNames.length === 0) {
    return { label: "Instructor", name: "Khusbu Gemmati" };
  }

  if (instructorNames.length === 1) {
    return { label: "Instructor", name: instructorNames[0] };
  }

  return {
    label: "Instructors",
    name: `${instructorNames[0]} + ${instructorNames.length - 1} more`,
  };
}

function getBundleCategories(bundle: BundleWithCourses, limit: number) {
  if (limit <= 0) return [];

  const categoryRanks = new Map<string, { count: number; firstSeenIndex: number }>();
  let firstSeenIndex = 0;

  bundle.courses.forEach((course) => {
    const courseCategoryNames = new Set<string>();

    (course.category ?? []).forEach((category) => {
      const name = category.name.trim();
      if (!name || name === signatureRoutineTagLabel || courseCategoryNames.has(name)) return;

      courseCategoryNames.add(name);
      const existing = categoryRanks.get(name);

      if (existing) {
        existing.count += 1;
      } else {
        categoryRanks.set(name, { count: 1, firstSeenIndex });
        firstSeenIndex += 1;
      }
    });
  });

  return Array.from(categoryRanks.entries())
    .sort((left, right) => right[1].count - left[1].count || left[1].firstSeenIndex - right[1].firstSeenIndex)
    .slice(0, limit)
    .map(([name]) => name);
}

function getBundleDuration(courses: CourseWithDetails[]) {
  const totalDuration = courses.reduce((sum, course) => sum + Number(course.total_duration ?? 0), 0);
  return totalDuration > 0 ? formatDuration(totalDuration) : null;
}

function getBundleTotalVideos(courses: CourseWithDetails[]) {
  const totalVideos = courses.reduce((sum, course) => sum + Number(course.total_videos ?? 0), 0);
  return totalVideos > 0 ? totalVideos : null;
}

function bundleHasCartConflict(bundle: BundleWithCourses, cartBundles: CartBundleItem[]) {
  const bundleCourseIds = new Set(bundle.courses.map((course) => course.id));

  return cartBundles.some(
    (item) =>
      item.bundle_id !== bundle.id &&
      item.course_ids.some((courseId) => bundleCourseIds.has(courseId)),
  );
}
